let nodeSequence = 0;

const nextNodeId = () => {
  nodeSequence += 1;
  return `condition-node-${nodeSequence}`;
};

const cloneValue = value => JSON.parse(JSON.stringify(value));

export const isConditionNode = node =>
  node && (node.type === 'condition' || node.attribute_key);

export const isGroupNode = node =>
  node &&
  !isConditionNode(node) &&
  (node.type === 'group' || Array.isArray(node.children));

export const normalizeOperator = operator => {
  const normalized = String(operator || 'and').toLowerCase();
  return normalized === 'or' ? 'or' : 'and';
};

export const createConditionNode = overrides => ({
  id: nextNodeId(),
  type: 'condition',
  ...cloneValue(overrides || {}),
});

export const createGroupNode = (operator = 'and', children = []) => ({
  id: nextNodeId(),
  type: 'group',
  operator: normalizeOperator(operator),
  children,
});

const collapseGroup = (operator, nodes) => {
  const validNodes = nodes.filter(Boolean);
  if (!validNodes.length) {
    return createGroupNode(operator, []);
  }

  if (validNodes.length === 1) {
    return validNodes[0];
  }

  return createGroupNode(operator, validNodes);
};

const normalizeLegacyConditions = (conditions, mapCondition) => {
  // Lista vazia sai como grupo 'and'. Sem esta saída antecipada ela desce até o
  // collapseGroup('or', []) do agrupador externo e o grupo raiz nasce OU — aí a
  // SEGUNDA condição que o usuário adicionar vira OR sem ele pedir. O backend
  // não tem esse viés (Conditions::Expression#empty_group devolve 'and') e o
  // editor de automações também nasce E.
  if (!conditions.length) {
    return createGroupNode('and', []);
  }

  const serializedConditions = conditions.map(condition =>
    createConditionNode(mapCondition(cloneValue(condition)))
  );

  const orGroups = [];
  let currentAndGroup = [];

  serializedConditions.forEach(condition => {
    currentAndGroup.push(condition);
    const queryOperator = String(condition.query_operator || '').toLowerCase();

    if (queryOperator === 'or' || !queryOperator) {
      orGroups.push(collapseGroup('and', currentAndGroup));
      currentAndGroup = [];
    }
  });

  if (currentAndGroup.length) {
    orGroups.push(collapseGroup('and', currentAndGroup));
  }

  const normalized = collapseGroup('or', orGroups);
  if (isGroupNode(normalized)) {
    return normalized;
  }

  return createGroupNode('and', [normalized]);
};

const normalizeNode = (node, mapCondition) => {
  if (!node) {
    return null;
  }

  if (Array.isArray(node)) {
    return normalizeLegacyConditions(node, mapCondition);
  }

  const normalizedNode = cloneValue(node);

  if (normalizedNode.payload) {
    return normalizeNode(normalizedNode.payload, mapCondition);
  }

  if (isConditionNode(normalizedNode)) {
    const mappedCondition = mapCondition(normalizedNode);
    return mappedCondition ? createConditionNode(mappedCondition) : null;
  }

  if (isGroupNode(normalizedNode)) {
    const children = (normalizedNode.children || [])
      .map(child => normalizeNode(child, mapCondition))
      .filter(Boolean);
    return createGroupNode(
      normalizeOperator(normalizedNode.operator),
      children
    );
  }

  return null;
};

export const normalizeToGroup = (
  node,
  {
    mapCondition = condition => condition,
    createDefaultCondition = () => createConditionNode({}),
    ensureAtLeastOneCondition = true,
  } = {}
) => {
  const normalized =
    normalizeNode(node, mapCondition) || createGroupNode('and', []);

  const result = isGroupNode(normalized)
    ? normalized
    : createGroupNode('and', [normalized]);

  if (ensureAtLeastOneCondition && !result.children.length) {
    result.children.push(createDefaultCondition());
  }

  return result;
};

const serializeValues = condition => {
  if (
    [
      'matches_date_parts',
      'not_matches_date_parts',
      'date_offset_elapsed',
      'date_offset_before',
      'date_offset_ago',
      'in_next',
      'in_last',
    ].includes(condition.filter_operator)
  ) {
    return Array.isArray(condition.values)
      ? cloneValue(condition.values)
      : condition.values
      ? [cloneValue(condition.values)]
      : [];
  }

  if (condition.attribute_key === 'content') {
    return [condition.values || ''];
  }

  if (
    condition.attribute_key === 'last_sent_messaged_at' ||
    condition.attribute_key === 'last_received_messaged_at'
  ) {
    return condition.values;
  }

  if (Array.isArray(condition.values)) {
    if (
      condition.values.length &&
      condition.values.every(
        value => typeof value === 'object' && value !== null
      )
    ) {
      const everyValueHasId = condition.values.every(value =>
        Object.prototype.hasOwnProperty.call(value, 'id')
      );

      if (!everyValueHasId) {
        return cloneValue(condition.values);
      }

      return condition.values.map(value => value.id);
    }

    return condition.values;
  }

  if (
    condition.values &&
    typeof condition.values === 'object' &&
    condition.values.type &&
    Object.prototype.hasOwnProperty.call(condition.values, 'value')
  ) {
    return condition.values;
  }

  if (typeof condition.values === 'boolean') {
    return [condition.values];
  }

  if (condition.values && typeof condition.values === 'object') {
    return [condition.values.id];
  }

  if (
    condition.values === null ||
    condition.values === undefined ||
    condition.values === ''
  ) {
    return [];
  }

  return [condition.values];
};

const serializeCondition = condition => {
  const payload = cloneValue(condition);
  delete payload.id;
  delete payload.type;
  delete payload.attribute_model;
  delete payload.input_type;
  delete payload.data_type;
  delete payload.query_operator;

  payload.values = serializeValues(condition);
  return payload;
};

export const serializeConditionTree = node => {
  if (!node) {
    return createGroupNode('and', []);
  }

  if (Array.isArray(node)) {
    return serializeConditionTree(
      normalizeLegacyConditions(node, condition => condition)
    );
  }

  if (isConditionNode(node)) {
    return serializeCondition(node);
  }

  const children = (node.children || [])
    .map(child => serializeConditionTree(child))
    .filter(Boolean);

  return {
    type: 'group',
    operator: normalizeOperator(node.operator),
    children,
  };
};

export const flattenConditionNodes = node => {
  if (!node) {
    return [];
  }

  if (Array.isArray(node)) {
    return node.flatMap(child => flattenConditionNodes(child));
  }

  if (isConditionNode(node)) {
    return [node];
  }

  return (node.children || []).flatMap(child => flattenConditionNodes(child));
};

export const getConditionCount = node => flattenConditionNodes(node).length;

export const hasConditions = node => getConditionCount(node) > 0;

export const getFirstConditionNode = node =>
  flattenConditionNodes(node)[0] || null;
